/**
 * ServicesRequestForm - Request professional services (Implementation PM, BA Scoping, Integration)
 * Available in Support AI Product tab and Help/Assist menu
 */

import React, { useState } from 'react';
import { 
  Briefcase, Calendar, Mail, User, Building2, Send, 
  CheckCircle, X, Loader2, FileText, Wrench, Users
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import { toast } from 'sonner';

const SERVICE_TYPES = [
  { 
    value: 'implementation_pm', 
    label: 'Implementation PM',
    icon: Briefcase,
    description: 'Project management for new deployments'
  },
  { 
    value: 'ba_scoping', 
    label: 'BA Scoping',
    icon: FileText,
    description: 'Business analysis and scope definition'
  },
  { 
    value: 'integration', 
    label: 'Integration',
    icon: Wrench,
    description: 'Technical integration consulting'
  }
];

const ServicesRequestForm = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  initialNeed = null 
}) => {
  const { user } = useAuth();
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    need: initialNeed || '',
    preferredStartDate: '',
    notes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.need) {
      setError('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await api.submitServiceRequest({
        name: formData.name,
        email: formData.email,
        tenantId: tenantId,
        need: formData.need,
        preferredStartDate: formData.preferredStartDate || null,
        notes: formData.notes || null
      });
      
      // Track telemetry
      trackEvent(TELEMETRY_EVENTS.SERVICES_REQUEST_SUBMITTED, {
        need: formData.need
      });
      
      setSubmitted(true);
      toast.success('Service request submitted successfully!');
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
    } catch (err) {
      console.error('Failed to submit service request:', err);
      setError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleClose = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      need: initialNeed || '',
      preferredStartDate: '',
      notes: ''
    });
    setSubmitted(false);
    setError(null);
    onClose?.();
  };
  
  if (!isOpen) return null;
  
  // Success state
  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="services-request-modal">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Request Submitted!</h3>
            <p className="text-gray-600 mb-6">
              Our team will review your request and reach out within 1-2 business days.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              A confirmation has been sent to <span className="font-medium">{formData.email}</span>
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
              data-testid="close-success-btn"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="services-request-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Request Services</h2>
              <p className="text-sm text-gray-500">Get help from our professional services team</p>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <User className="w-4 h-4 inline mr-1.5 text-gray-400" />
              Your Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
              placeholder="John Smith"
              required
              data-testid="name-input"
            />
          </div>
          
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <Mail className="w-4 h-4 inline mr-1.5 text-gray-400" />
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
              placeholder="john@company.com"
              required
              data-testid="email-input"
            />
          </div>
          
          {/* Tenant (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <Building2 className="w-4 h-4 inline mr-1.5 text-gray-400" />
              Organization
            </label>
            <input
              type="text"
              value={user?.tenant?.name || 'Your Organization'}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
              disabled
            />
          </div>
          
          {/* Area of Need */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Area of Need *
            </label>
            <div className="grid grid-cols-1 gap-2">
              {SERVICE_TYPES.map(service => (
                <label
                  key={service.value}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.need === service.value 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                  data-testid={`service-option-${service.value}`}
                >
                  <input
                    type="radio"
                    name="need"
                    value={service.value}
                    checked={formData.need === service.value}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <service.icon className={`w-5 h-5 ${
                    formData.need === service.value ? 'text-purple-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className={`font-medium ${
                      formData.need === service.value ? 'text-purple-700' : 'text-gray-800'
                    }`}>
                      {service.label}
                    </div>
                    <div className="text-xs text-gray-500">{service.description}</div>
                  </div>
                  {formData.need === service.value && (
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                  )}
                </label>
              ))}
            </div>
          </div>
          
          {/* Preferred Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <Calendar className="w-4 h-4 inline mr-1.5 text-gray-400" />
              Preferred Start Date
            </label>
            <input
              type="date"
              name="preferredStartDate"
              value={formData.preferredStartDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
              data-testid="start-date-input"
            />
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Additional Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
              placeholder="Tell us more about your project or requirements..."
              data-testid="notes-input"
            />
          </div>
          
          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !formData.name || !formData.email || !formData.need}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-request-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServicesRequestForm;
